import React, { useEffect, useRef } from 'react';
import { FiAlertCircle, FiX } from 'react-icons/fi';
import './BlockUserModal.css';

/**
 * BlockUserModal - Star Citizen themed confirmation modal for blocking users
 *
 * Props:
 * - isOpen (boolean): Controls modal visibility
 * - userName (string): Display name of user to block
 * - userId (string): UUID of user to block
 * - onConfirm (function): Callback when user confirms block
 * - onCancel (function): Callback when user cancels
 *
 * Features:
 * - Escape key to close
 * - Click overlay to close
 * - Focus trap (focuses confirm button on open)
 * - Prevents body scroll when open
 * - ARIA labels for accessibility
 */
export default function BlockUserModal({ isOpen, userName, userId, onConfirm, onCancel }) {
    const confirmButtonRef = useRef(null);
    const modalRef = useRef(null);

    // Focus trap and escape key handler
    useEffect(() => {
        if (!isOpen) return;

        // Focus confirm button on open
        if (confirmButtonRef.current) {
            confirmButtonRef.current.focus();
        }

        // Prevent body scroll
        document.body.style.overflow = 'hidden';

        // Escape key handler
        const handleEscape = (e) => {
            if (e.key === 'Escape') {
                console.log('BlockUserModal: Escape key pressed, closing modal');
                onCancel();
            }
        };

        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = '';
        };
    }, [isOpen, onCancel]);

    // Focus trap within modal
    useEffect(() => {
        if (!isOpen || !modalRef.current) return;

        const modal = modalRef.current;
        const focusableElements = modal.querySelectorAll(
            'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        const handleTab = (e) => {
            if (e.key !== 'Tab') return;

            if (e.shiftKey) {
                if (document.activeElement === firstElement) {
                    e.preventDefault();
                    lastElement.focus();
                }
            } else {
                if (document.activeElement === lastElement) {
                    e.preventDefault();
                    firstElement.focus();
                }
            }
        };

        modal.addEventListener('keydown', handleTab);

        return () => {
            modal.removeEventListener('keydown', handleTab);
        };
    }, [isOpen]);

    const handleConfirmClick = () => {
        console.log('BlockUserModal: User confirmed block for userId:', userId);
        onConfirm(userId);
    };

    const handleCancelClick = () => {
        console.log('BlockUserModal: User cancelled block');
        onCancel();
    };

    const handleOverlayClick = () => {
        console.log('BlockUserModal: Overlay clicked, closing modal');
        onCancel();
    };

    if (!isOpen) return null;

    return (
        <div
            className="block-modal-overlay"
            onClick={handleOverlayClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="block-modal-title"
            aria-describedby="block-modal-description"
        >
            <div
                className="block-modal-container"
                onClick={(e) => e.stopPropagation()}
                ref={modalRef}
            >
                <button
                    className="block-modal-close"
                    onClick={handleCancelClick}
                    aria-label="Close modal"
                >
                    <FiX />
                </button>

                <div className="block-modal-header">
                    <div className="block-modal-icon-wrapper">
                        <FiAlertCircle className="block-modal-icon" />
                    </div>
                    <h2 id="block-modal-title" className="block-modal-title">
                        Block @{userName}?
                    </h2>
                </div>

                <div className="block-modal-body">
                    <div id="block-modal-description" className="block-modal-description">
                        <p className="block-modal-warning">
                            This action will implement a shadow block:
                        </p>
                        <ul className="block-modal-effects">
                            <li>
                                <span className="effect-icon">•</span>
                                They won't be able to send you direct messages
                            </li>
                            <li>
                                <span className="effect-icon">•</span>
                                You won't see their content in your feed
                            </li>
                            <li>
                                <span className="effect-icon">•</span>
                                They won't know they've been blocked
                            </li>
                        </ul>
                    </div>
                </div>

                <div className="block-modal-footer">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={handleCancelClick}
                        tabIndex={0}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-block"
                        onClick={handleConfirmClick}
                        ref={confirmButtonRef}
                        tabIndex={0}
                    >
                        Block User
                    </button>
                </div>
            </div>
        </div>
    );
}
